import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, MapPin, CheckCircle2, History, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, eur, CATEGORY_LABELS } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import OrderCountdown from "@/components/OrderCountdown";
import { getDeadline } from "@/lib/deadlines";
import { useOrderedQuantities } from "@/lib/useOrderedQuantities";
import { pressZoom } from "@/lib/usePressZoom";
import ImageZoom from "@/components/ImageZoom";
import TierPricing from "@/components/TierPricing";
import { useDiscounts, tierRate } from "@/lib/useDiscounts";

export default function WineDetail({ wine, onClose, onDeleted }) {
  const { add, setQty, items } = useCart();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const orderedMap = useOrderedQuantities();
  const [qty, setQtyLocal] = useState(1);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomHover, setZoomHover] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deadline = wine ? getDeadline(wine.producer) : null;

  const removeWine = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/wines/${wine.id}`);
      toast.success(`« ${wine.name} » supprimé de la cave`);
      onDeleted?.(wine.id);
      onClose();
    } catch {
      toast.error("Suppression impossible — réessayez.");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };
  const orderedQty = wine ? orderedMap[wine.id] || 0 : 0;
  const inCartQty = wine ? items.find((i) => i.wine.id === wine.id)?.qty || 0 : 0;
  const inCart = inCartQty > 0;
  const effectiveQty = inCart ? inCartQty : qty;

  // Prix unitaire réellement appliqué avec la remise panachée du viticulteur (tient compte des autres vins de la maison déjà dans la commande)
  const tiersMap = useDiscounts();
  const tiers = wine ? tiersMap[wine.producer] : null;
  const mixBase = wine ? items.filter((i) => i.wine.producer === wine.producer && i.wine.id !== wine.id).reduce((s, i) => s + i.qty, 0) : 0;
  const mixQty = mixBase + effectiveQty;
  const appliedRate = tierRate(tiers, mixQty);
  const appliedUnit = wine ? wine.price - appliedRate : 0;

  // Suivi statistiques : le client visite cette fiche (maison visitée)
  useEffect(() => {
    if (!wine) return;
    api.post("/track/visit", { wine_name: wine.name, producer: wine.producer }).catch(() => {});
  }, [wine?.id]);

  const dec = () => {
    if (inCart) setQty(wine.id, inCartQty - 1);
    else setQtyLocal((q) => Math.max(1, q - 1));
  };
  const inc = () => {
    if (inCart) setQty(wine.id, Math.min(wine.stock || 99, inCartQty + 1));
    else setQtyLocal((q) => Math.min(wine.stock || 99, q + 1));
  };

  return (
    <AnimatePresence>
      {wine && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="wine-detail-modal"
        >
          <div className="absolute inset-0 bg-[#1C1917]/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            className="relative w-full sm:max-w-lg bg-[#FAF6F0] rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] overflow-y-auto no-scrollbar"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-lenis-prevent
          >
            <div className="relative pt-12 pb-2 px-8">
              {isAdmin && (
                <button
                  onClick={removeWine}
                  disabled={deleting}
                  className={`absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[0.65rem] font-semibold tracking-wide transition-colors ${
                    confirmDelete
                      ? "bg-[#6B1226] text-[#FAF6F0] hover:bg-[#580E1D]"
                      : "bg-white/80 text-[#6B1226] hover:bg-white"
                  }`}
                  data-testid="wine-detail-delete-button"
                  aria-label={confirmDelete ? "Confirmer la suppression" : "Supprimer ce vin"}
                  title="Supprimer ce vin de la cave"
                >
                  <Trash2 size={15} />
                  {confirmDelete && (deleting ? "Suppression…" : "Confirmer la suppression ?")}
                </button>
              )}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/80 text-[#1C1917] hover:bg-white transition-colors z-10"
                data-testid="wine-detail-close-button"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
              <div className="flex justify-center">
                <img
                  src={wine.image}
                  alt={wine.name}
                  {...pressZoom(() => {
                    setZoomHover(true);
                    setZoomSrc(wine.image);
                  })}
                  onClick={() => {
                    setZoomHover(false);
                    setZoomSrc(wine.image);
                  }}
                  className="h-64 sm:h-72 w-auto object-contain no-frame cursor-zoom-in bottle-hover"
                  data-testid="wine-detail-image"
                />
              </div>
            </div>

            <div className="px-6 sm:px-8 pb-8">
              <p className="font-eyebrow text-[0.6rem] tracking-[0.3em] uppercase text-[#C5A059]">
                {CATEGORY_LABELS[wine.category]} {wine.year ? `· Millésime ${wine.year}` : ""}
              </p>
              <h3 className="font-display text-3xl font-semibold text-[#1C1917] mt-1.5">{wine.name}</h3>

              {deadline && (
                <div className="mt-3">
                  <OrderCountdown deadline={deadline} label={deadline.label} />
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {wine.appellation && (
                  <span className="bg-[#F5EEDC] text-[#8C6D23] border border-[#C5A059]/40 rounded-full px-3 py-1 text-[0.65rem] font-semibold tracking-widest uppercase">
                    {wine.appellation}
                  </span>
                )}
                {wine.region && (
                  <span className="inline-flex items-center gap-1 bg-white border border-[#6B1226]/15 rounded-full px-3 py-1 text-[0.65rem] font-semibold tracking-widest uppercase text-[#6B1226]">
                    <MapPin size={11} /> {wine.region}
                  </span>
                )}
                <span
                  className={`rounded-full px-3 py-1 text-[0.65rem] font-semibold tracking-widest uppercase ${
                    wine.stock > 5
                      ? "bg-[#3F6212]/10 text-[#3F6212] border border-[#3F6212]/30"
                      : wine.stock > 0
                        ? "bg-[#F5EEDC] text-[#8C6D23] border border-[#C5A059]/40"
                        : "bg-stone-200 text-stone-500"
                  }`}
                  data-testid="wine-detail-stock"
                >
                  {wine.stock > 5 ? "En stock" : wine.stock > 0 ? `Plus que ${wine.stock}` : "Épuisé"}
                </span>
              </div>

              {wine.description && (
                <p className="mt-5 text-sm sm:text-base text-[#44403C] leading-relaxed font-light" data-testid="wine-detail-tasting-notes">
                  {wine.description}
                </p>
              )}

              {(() => {
                const tech = [
                  ["Contenance", wine.volume],
                  ["Degré", wine.alcohol_degree],
                  ["Cépages", wine.cepages],
                  ["Garde", wine.garde],
                  ["Service", wine.temperature_service],
                  ["Allergènes", wine.allergenes],
                ].filter(([, v]) => v);
                if (!tech.length) return null;
                return (
                  <dl className="mt-5 bg-white rounded-2xl border border-[#6B1226]/10 p-4 grid grid-cols-2 gap-x-4 gap-y-2.5" data-testid="wine-detail-tech">
                    {tech.map(([label, value]) => (
                      <div key={label}>
                        <dt className="font-eyebrow text-[0.55rem] tracking-[0.2em] uppercase text-[#C5A059]">{label}</dt>
                        <dd className="text-sm text-[#1C1917] mt-0.5">{value}</dd>
                      </div>
                    ))}
                  </dl>
                );
              })()}

              {orderedQty > 0 && (
                <div
                  className="mt-4 flex items-center gap-2.5 bg-[#F5EEDC] border border-[#C5A059]/40 rounded-2xl px-4 py-3"
                  data-testid="wine-detail-ordered-qty"
                >
                  <History size={17} className="text-[#8C6D23] shrink-0" />
                  <p className="text-sm text-[#1C1917] leading-snug">
                    <span className="font-semibold text-[#8C6D23]">
                      {orderedQty} bouteille{orderedQty > 1 ? "s" : ""} déjà commandée{orderedQty > 1 ? "s" : ""}
                    </span>
                    <br />
                    <span className="text-xs text-[#57534E]">Total de vos commandes passées pour ce vin.</span>
                  </p>
                </div>
              )}

              {inCart && (
                <div
                  className="mt-6 flex items-center gap-2.5 bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-2xl px-4 py-3"
                  data-testid="wine-detail-in-cart-badge"
                >
                  <CheckCircle2 size={18} className="text-[#6B1226] shrink-0" />
                  <p className="text-sm text-[#1C1917] leading-snug">
                    <span className="font-semibold text-[#6B1226]">
                      {inCartQty} bouteille{inCartQty > 1 ? "s" : ""} dans ma commande
                    </span>
                    <br />
                    <span className="text-xs text-[#57534E]">Ajustez la quantité ci-dessous, à tout moment.</span>
                  </p>
                </div>
              )}

              <TierPricing wine={wine} />

              <div className="mt-7 flex items-center justify-between gap-4">
                <div className="flex items-center border border-[#6B1226]/20 rounded-full overflow-hidden" data-testid="wine-detail-quantity-input">
                  <button onClick={dec} className="p-2.5 px-3.5 text-[#6B1226] hover:bg-[#6B1226]/5" data-testid="wine-detail-qty-minus" aria-label="Moins">
                    <Minus size={15} />
                  </button>
                  <span className="w-8 text-center font-semibold text-[#1C1917]">{effectiveQty}</span>
                  <button onClick={inc} className="p-2.5 px-3.5 text-[#6B1226] hover:bg-[#6B1226]/5" data-testid="wine-detail-qty-plus" aria-label="Plus">
                    <Plus size={15} />
                  </button>
                </div>
                <div className="text-right">
                  {appliedRate > 0 && (
                    <span className="block text-sm line-through text-[#A8A29E]">{eur(wine.price * effectiveQty)}</span>
                  )}
                  <span className="price text-3xl text-[#6B1226]">{eur(appliedUnit * effectiveQty)}</span>
                  {appliedRate > 0 && (
                    <span className="block text-[0.6rem] font-semibold text-[#8C6D23]">
                      {eur(appliedUnit)} /bt — prix dégressif appliqué
                    </span>
                  )}
                </div>
              </div>

              {inCart ? (
                <button
                  onClick={() => {
                    toast.success("Quantité mise à jour dans ma commande");
                    onClose();
                  }}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold tracking-wide transition-colors bg-[#6B1226] text-[#FAF6F0] hover:bg-[#580E1D]"
                  data-testid="wine-detail-add-button"
                >
                  <CheckCircle2 size={17} /> Parfait, c'est noté
                </button>
              ) : (
                <button
                  onClick={() => {
                    add(wine, qty);
                    toast.success(`${qty} × ${wine.name} ajouté au panier`);
                    onClose();
                  }}
                  disabled={wine.stock <= 0}
                  className={`mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold tracking-wide transition-colors ${
                    wine.stock <= 0
                      ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                      : "bg-[#6B1226] text-[#FAF6F0] hover:bg-[#580E1D]"
                  }`}
                  data-testid="wine-detail-add-button"
                >
                  <ShoppingBag size={17} /> Ajouter à ma commande
                </button>
              )}

              {inCart && (
                <button
                  onClick={() => {
                    setQty(wine.id, 0);
                    toast.success(`« ${wine.name} » retiré de votre commande`);
                  }}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-xs font-semibold border border-stone-300 text-[#57534E] hover:border-[#6B1226] hover:text-[#6B1226] transition-colors"
                  data-testid="wine-detail-reset-button"
                  aria-label="Remettre cette bouteille à zéro"
                >
                  <RotateCcw size={13} /> Remettre cette bouteille à zéro
                </button>
              )}

              <p className="mt-4 text-center text-[0.68rem] italic text-[#A8A29E]" data-testid="wine-detail-availability-note">
                Quantités proposées sous réserve de disponibilité en cave.
              </p>
            </div>
          </motion.div>
          <ImageZoom src={zoomSrc} alt={wine?.name || "Bouteille"} viaHover={zoomHover} onStick={() => setZoomHover(false)} onClose={() => setZoomSrc(null)} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
